import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Flex, HStack, Icon, Image, Spinner, Text, VStack, useToast,
} from '@chakra-ui/react';
import { FiNavigation, FiSliders } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bounty, BountyClaim, normalizeBountyCompanyName } from '@foodfluence/shared';
import { useBountyData } from '../../contexts/BountyDataContext';
import { InfluencerDataContext } from '../../contexts/InfluencerDataContext';
import { InfluencerCampaignDataContext } from '../../contexts/InfluencerCampaignDataContext';
import { CollabDataContext } from '../../contexts/collabDataContext';
import { useInfluencerLocations } from '../../contexts/InfluencerLocationContext';
import { getInfluencerPrimaryLocation, hasValidCoordinates } from '../../utils/locationUtils';
import { ff } from '../../theme';
import { DisplayHeading, PillSearch, UnderlineTabs } from '../../components/ui';
import {
  boardLocationLabel,
  boardLocationToInfluencerLocations,
  influencerLocationToBoardLocation,
  loadBoardNearMeLocation,
  loadExploreLocationFallback,
  saveBoardNearMeLocation,
  type BoardNearMeLocation,
} from './boardLocationUtils';
import { calculatePendingPayoutTotal } from '../wallet/utils/walletUtils';
import BountyBoardList from './components/BountyBoardList';
import SubmitBountyBanner from './components/SubmitBountyBanner';
import MyBountiesList from './components/MyBountiesList';
import Chip from './components/Chip';
import NearMeChip from './components/NearMeChip';
import WorkedWithCard from './components/WorkedWithCard';
import { bountyApi } from '../../services/api/bounties';
import {
  BoardCategoryFilter, boardEmptyCopy, claimRoutePath, filterBoardBounties, isRecommended, myBountiesActionCount,
} from './utils';
import { pageBottomPadding } from './theme';
import { CATEGORY_ICONS } from './categoryIcons';
import rewardsIcon from './assets/rewards.png';
import emptyMark from './assets/empty-mark.png';

type BoardChip = { key: BoardCategoryFilter; label: string };

const CATEGORY_CHIPS: BoardChip[] = [
  { key: 'all', label: 'All' },
  { key: 'cpgBrand', label: 'CPG Brands' },
  { key: 'restaurant', label: 'Restaurants' },
  { key: 'hotelsLodging', label: 'Hotels & lodging' },
];

/** Hidden behind the "More filters" chip until the creator expands the row. */
const OVERFLOW_CHIPS: BoardChip[] = [
  { key: 'other', label: 'Other' },
  { key: 'highPaying', label: 'High paying' },
];

/** Board empty card: grey panel, ink target mark, condensed title, optional action. */
const BoardEmpty: React.FC<{
  title?: string;
  body: string;
  action?: { label: string; onClick: () => void; outline?: boolean };
}> = ({ title, body, action }) => (
  <VStack spacing="10px" py="32px" px="24px" borderRadius="24px" bg={ff.grey} textAlign="center">
    <Image src={emptyMark} alt="" w="64px" h="64px" objectFit="contain" flex="none" />
    {title && <Text textStyle="display" fontSize="30px" lineHeight={1.12}>{title}</Text>}
    <Text maxW="270px" fontSize="14px" lineHeight="1.4" color="#55555A">{body}</Text>
    {action && (
      <Button
        variant={action.outline ? 'inkOutline' : 'ink'}
        mt="8px"
        h="48px"
        px="20px"
        borderRadius="full"
        fontSize="15px"
        color={action.outline ? ff.ink : ff.lime}
        bg={action.outline ? 'transparent' : ff.ink}
        onClick={action.onClick}
      >
        {action.label}
      </Button>
    )}
  </VStack>
);

const BountiesPage: React.FC = () => {
  const { bounties, claims, invites, loading, refetchBounties } = useBountyData();
  const { influencerData } = useContext(InfluencerDataContext);
  const { influencerLocations } = useInfluencerLocations();
  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const [searchParams] = useSearchParams();
  const [segment, setSegment] = useState<'board' | 'mine'>(() =>
    searchParams.get('tab') === 'mine' ? 'mine' : 'board',
  );
  const [category, setCategory] = useState<BoardCategoryFilter>('all');
  const [nearMe, setNearMe] = useState(false);
  const [boardLocation, setBoardLocation] = useState<BoardNearMeLocation | null>(() =>
    loadBoardNearMeLocation() ?? loadExploreLocationFallback(),
  );
  const [showMoreChips, setShowMoreChips] = useState(false);
  const [search, setSearch] = useState('');
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [workedWithDismissed, setWorkedWithDismissed] = useState(false);
  const [openNearMePicker, setOpenNearMePicker] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const openSegment = (key: 'board' | 'mine') => {
    setSegment(key);
    navigate(key === 'mine' ? '/bounties?tab=mine' : '/bounties', { replace: true });
  };

  const claimsByBounty = useMemo(() => {
    const map: Record<string, BountyClaim> = {};
    claims.forEach(c => { map[c.bountyID] = c; });
    return map;
  }, [claims]);

  const brands = useMemo(() => influencerData?.brands ?? [], [influencerData?.brands]);
  const validLocations = useMemo(() => influencerLocations.filter(hasValidCoordinates), [influencerLocations]);
  const primaryLocation = useMemo(() => getInfluencerPrimaryLocation(validLocations), [validLocations]);
  const seededPrimaryLocation = useRef(false);

  useEffect(() => {
    if (seededPrimaryLocation.current || boardLocation || !primaryLocation) return;
    seededPrimaryLocation.current = true;
    setBoardLocation(influencerLocationToBoardLocation(primaryLocation));
  }, [boardLocation, primaryLocation]);

  const nearMeLocations = useMemo(() => {
    if (boardLocation) return boardLocationToInfluencerLocations(boardLocation);
    if (nearMe) return [];
    return validLocations;
  }, [boardLocation, nearMe, validLocations]);

  const nearMeLabel = useMemo(
    () => boardLocationLabel(boardLocation, primaryLocation),
    [boardLocation, primaryLocation],
  );

  const handleBoardLocationChange = (location: BoardNearMeLocation | null, radius: number) => {
    const next = location ? { ...location, radius } : null;
    setBoardLocation(next);
    saveBoardNearMeLocation(next);
    setOpenNearMePicker(false);
    if (!next) setNearMe(false);
  };

  const handleNearMeToggle = () => {
    setNearMe(prev => {
      const next = !prev;
      if (!next) {
        setOpenNearMePicker(false);
        return next;
      }
      if (boardLocation) return next;
      if (primaryLocation) {
        const seeded = influencerLocationToBoardLocation(primaryLocation);
        setBoardLocation(seeded);
        saveBoardNearMeLocation(seeded);
        return next;
      }
      setOpenNearMePicker(true);
      return next;
    });
  };

  const hasWalletBalance = calculatePendingPayoutTotal(collabData, campaignData, [], claims) > 0;
  const mineBadge = useMemo(() => myBountiesActionCount(bounties, claims), [bounties, claims]);

  const activeBounties = useMemo(() => bounties.filter(b => b.status === 'active'), [bounties]);

  const invitedBountyIds = useMemo(
    () => invites.filter(i => i.status === 'sent').map(i => i.bountyID),
    [invites]
  );
  const recommendedBountyIds = useMemo(
    () => (brands.length > 0 ? activeBounties.filter(b => isRecommended(b, brands)).map(b => b.id) : []),
    [activeBounties, brands]
  );

  const unmatchedBrands = useMemo(() => {
    if (brands.length === 0) return [];
    return brands.filter(brand => !activeBounties.some(b => normalizeBountyCompanyName(b.companyName) === normalizeBountyCompanyName(brand)));
  }, [brands, activeBounties]);

  const visibleBounties = useMemo(
    () => filterBoardBounties(activeBounties, {
      search,
      category,
      nearMe,
      locations: nearMeLocations,
      invitedIds: invitedBountyIds,
      recommendedIds: recommendedBountyIds,
    }),
    [activeBounties, search, category, nearMe, nearMeLocations, invitedBountyIds, recommendedBountyIds]
  );

  const emptyCopy = boardEmptyCopy(activeBounties.length);

  const showWorkedWithCard =
    segment === 'board' && category === 'all' && !nearMe && !workedWithDismissed && unmatchedBrands.length > 0;

  const handleToggleMoreChips = () => {
    setShowMoreChips(prev => {
      // Collapsing the row must not leave a now-hidden chip selected.
      if (prev && OVERFLOW_CHIPS.some(chip => chip.key === category)) setCategory('all');
      return !prev;
    });
  };

  const clearFilters = () => {
    setCategory('all');
    setSearch('');
    setShowMoreChips(false);
  };

  const handleClaim = async (bounty: Bounty) => {
    if (!influencerData?.id) return;
    setClaimingId(bounty.id);
    try {
      const res = await bountyApi.claimBounty(influencerData.id, bounty.id);
      await refetchBounties();
      navigate(`/bounties/claims/${res.claimId}/claimed`);
    } catch (error) {
      toast({
        title: 'Could not claim bounty',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error', duration: 5000, isClosable: true,
      });
      refetchBounties();
    } finally {
      setClaimingId(null);
    }
  };

  const renderChip = (chip: BoardChip) => (
    <Chip
      key={chip.key}
      label={chip.label}
      icon={CATEGORY_ICONS[chip.key]}
      selected={category === chip.key}
      onClick={() => setCategory(chip.key)}
    />
  );

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} lineHeight="normal" pt="24px" pb={pageBottomPadding} overflowX="hidden">
      <VStack spacing="14px" align="stretch" px="20px">
        <Flex align="flex-end" justify="space-between" gap="12px">
          <DisplayHeading>Bounties</DisplayHeading>
          <Box position="relative" flexShrink={0}>
            <Button
              h="44px"
              pl="12px"
              pr="14px"
              borderRadius="full"
              border="1.5px solid"
              borderColor={ff.ink}
              bg="white"
              color={ff.ink}
              fontSize="14px"
              fontWeight={600}
              leftIcon={<Image src={rewardsIcon} alt="" w="22px" h="22px" objectFit="contain" flex="none" />}
              iconSpacing="6px"
              _hover={{ bg: ff.grey }}
              _active={{ bg: ff.grey }}
              onClick={() => navigate('/wallet')}
            >
              Rewards
            </Button>
            {hasWalletBalance && (
              <Box
                position="absolute"
                top="-2px"
                right="-1px"
                w="12px"
                h="12px"
                borderRadius="full"
                bg={ff.lime}
                border="2px solid"
                borderColor={ff.ink}
                pointerEvents="none"
                aria-hidden
              />
            )}
          </Box>
        </Flex>
        <Text fontSize="16px" lineHeight="1.4" color="#3E3E42">
          Know someone? Make the intro,{' '}
          <Text as="span" bg={ff.lime} color={ff.ink} fontWeight={600} px="4px" borderRadius="4px">get paid.</Text>
        </Text>

        <UnderlineTabs h="48px"
          tabs={[
            { key: 'board', label: 'Board' },
            { key: 'mine', label: 'My Bounties', badge: mineBadge || undefined },
          ]}
          active={segment}
          onChange={key => openSegment(key === 'mine' ? 'mine' : 'board')}
        />
      </VStack>

      {segment === 'board' ? (
        <VStack spacing="14px" align="stretch" pt="16px">
          <HStack
            spacing="8px"
            overflowX="auto"
            overflowY="hidden"
            px="20px"
            sx={{ '::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
          >
            <NearMeChip
              selected={nearMe}
              location={boardLocation}
              onToggle={handleNearMeToggle}
              onLocationChange={handleBoardLocationChange}
              openPickerOnMount={openNearMePicker}
            />
            {CATEGORY_CHIPS.map(renderChip)}
            {showMoreChips && OVERFLOW_CHIPS.map(renderChip)}
            <Button
              flex="none"
              h="44px"
              px="14px"
              borderRadius="full"
              border="1px dashed"
              borderColor={ff.muted}
              bg="white"
              color={ff.ink}
              fontSize="14px"
              fontWeight={600}
              leftIcon={<Icon as={FiSliders} boxSize="16px" />}
              iconSpacing="6px"
              _hover={{ bg: ff.grey }}
              aria-expanded={showMoreChips}
              onClick={handleToggleMoreChips}
            >
              {showMoreChips ? 'Fewer' : 'More filters'}
            </Button>
          </HStack>

          <VStack spacing="14px" align="stretch" px="20px">
            <PillSearch
              aria-label="Search brands or businesses"
              placeholder="Search brands or businesses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />

            {showWorkedWithCard && (
              <WorkedWithCard brands={unmatchedBrands} onDismiss={() => setWorkedWithDismissed(true)} />
            )}

            {nearMe && boardLocation && !loading && (
              <HStack spacing="6px">
                <Icon as={FiNavigation} color={ff.text2} boxSize="13px" />
                <Text fontSize="13px" color={ff.text2}>
                  {visibleBounties.length === 1 ? '1 bounty nearby' : `${visibleBounties.length} bounties nearby`}
                </Text>
              </HStack>
            )}

            {loading ? (
              <Box textAlign="center" py="40px"><Spinner color={ff.muted} /></Box>
            ) : visibleBounties.length > 0 ? (
              <BountyBoardList
                bounties={visibleBounties}
                claimsByBounty={claimsByBounty}
                recommendedBountyIds={recommendedBountyIds}
                invitedBountyIds={invitedBountyIds}
                claimingId={claimingId}
                onClaim={handleClaim}
                onViewClaim={c => navigate(claimRoutePath(c))}
              />
            ) : nearMe && !boardLocation ? (
              <BoardEmpty body="Choose a city above to see bounties near you." />
            ) : nearMe && boardLocation ? (
              <BoardEmpty
                body={`No bounties near ${nearMeLabel} yet — try another city or check the full board.`}
                action={{ label: 'Submit a bounty', onClick: () => navigate('/bounties/propose') }}
              />
            ) : (
              <BoardEmpty
                title={emptyCopy.title}
                body={emptyCopy.body}
                action={activeBounties.length > 0
                  ? { label: 'Clear filters', onClick: clearFilters, outline: true }
                  : { label: 'Submit a bounty', onClick: () => navigate('/bounties/propose') }}
              />
            )}

            <SubmitBountyBanner />
          </VStack>
        </VStack>
      ) : (
        <Box px="20px">
          <MyBountiesList />
        </Box>
      )}
    </Box>
  );
};

export default BountiesPage;
